import { canonicalCardHex16, isHexCardFormat } from './types/card-format';

function firstDefined(...values: any[]): any {
    return values.find(v => v !== null && v !== undefined);
}

function isCardNumberPart(value: any): boolean {
    if (value === null || value === undefined || String(value).trim() === '') {
        return false;
    }
    const n = Number(value);
    return Number.isFinite(n) && n >= 0;
}

/**
 * Numero de tarjeta para mostrar: el hex en los formatos hex (Mifare, Universal...) y
 * "FC<sep>codigo" en 26-bit. Acepta las distintas formas en que el API entrega la tarjeta
 * (card_format/format, card_hex/hex, facility_code/facilityCode, card_code/code).
 * Sin numero valido devuelve ''.
 */
export function cardNumberLabel(card: any, sep: string = '-'): string {
    if (!card) {
        return '';
    }
    const format = firstDefined(card.card_format, card.format);
    const hex = firstDefined(card.card_hex, card.hex);
    if (isHexCardFormat(format)) {
        return hex ? String(hex).toUpperCase() : '';
    }
    const fc = firstDefined(card.facility_code, card.facilityCode);
    const code = firstDefined(card.card_code, card.code);
    if (isCardNumberPart(fc) && isCardNumberPart(code)) {
        return `${fc}${sep}${code}`;
    }
    return '';
}

/**
 * Credencial de un evento de puerta (Live Access Monitor y reporte de accesos): el hex en los
 * formatos hex (Universal/Mifare/DESFire EV2, tambien de tarjetas no registradas en Goutru) y
 * "FC<sep>codigo" en 26-bit. Los centinelas (-1 y 0, "no hubo tarjeta") no se pintan: se exige
 * > 0 y no un truthy, porque -1 en JS es truthy y se veia "-1--1". Sin credencial devuelve ''.
 */
export function doorEventCardLabel(row: any, sep: string = '-'): string {
    if (!row) {
        return '';
    }
    if (isHexCardFormat(firstDefined(row.card_format, row.format)) || (row.facility_code > 0 && row.card_code > 0)) {
        return cardNumberLabel(row, sep);
    }
    return '';
}

/**
 * Tarjeta del ultimo acceso en la barra superior ("Card ..."): el hex en los formatos hex y,
 * en 26-bit, solo el codigo como siempre (sin FC). Los centinelas (-1 y 0: app, titular
 * desconocido, sin tarjeta) no se pintan; antes se veia "Card -1".
 */
export function lastEntryCardLabel(row: any): string {
    if (!row) {
        return '';
    }
    if (isHexCardFormat(firstDefined(row.card_format, row.format))) {
        return cardNumberLabel(row);
    }
    const code = firstDefined(row.card_code, row.code);
    return isCardNumberPart(code) && Number(code) > 0 ? String(code).trim() : '';
}

/**
 * Clave de orden de la columna del numero de tarjeta: las hex por su valor ('H'), las 26-bit
 * por FC y codigo numericos ('W') y al final las que no tienen numero ('Z').
 */
export function cardSortKey(card: any): string {
    if (!card) {
        return 'Z';
    }
    const format = firstDefined(card.card_format, card.format);
    if (isHexCardFormat(format)) {
        const hex = firstDefined(card.card_hex, card.hex);
        return hex ? 'H' + canonicalCardHex16(hex) : 'Z';
    }
    const fc = firstDefined(card.facility_code, card.facilityCode);
    const code = firstDefined(card.card_code, card.code);
    if (isCardNumberPart(fc) && isCardNumberPart(code)) {
        return 'W' + String(Number(fc)).padStart(5, '0') + String(Number(code)).padStart(10, '0');
    }
    return 'Z';
}

/**
 * Lo que devuelve el modal de alta de tarjeta al cerrarse: `{ card: uuid, label }`
 * (antes era solo la etiqueta "FC-codigo"). Etiqueta para buscarla en el API.
 */
export function createdCardLabel(created: any): string {
    if (!created) {
        return '';
    }
    return typeof created === 'string' ? created : created.label || '';
}

/**
 * La tarjeta recien creada dentro de una lista de opciones (con `card` = uuid y `card_description`):
 * por uuid y, si el API no lo informo, por la etiqueta. null si no esta.
 */
export function findCreatedCard(cards: any[], created: any): any {
    if (!created || !cards) {
        return null;
    }
    const uuid = typeof created === 'object' ? created.card : null;
    const label = createdCardLabel(created);
    return (uuid && cards.find(c => c.card === uuid))
        || (label && cards.find(c => c.card_description === label))
        || null;
}
